// State management
import {
    getAllHistoryEntries,
    putHistoryEntry,
    deleteHistoryEntry,
    putAllHistoryEntries,
    clearHistoryStore,
    migrateFromLocalStorage
} from './services/history-db.js';

// Set once we've shown the quota-exceeded dialog this session, so repeated
// keystroke-triggered saves don't spam the user with alerts.
let quotaAlertShown = false;

// Set once a cloud snapshot (or an explicit wipe) has taken ownership of
// state.history. The IndexedDB load in initHistoryStorage() is asynchronous and
// races the sign-in path; when it loses that race its entries are stale, and on
// a shared browser they may belong to a DIFFERENT account. Adopting them at that
// point would not just show the wrong tournaments — saveToLocalStorage() writes
// state.history straight back to the signed-in user's document, publishing them.
let historyOwnedByCloud = false;

export const state = {
    activeTournament: null,
    players: [],
    rounds: [],
    history: [],
    editingPlayerId: null,
    isSpectatorMode: false,
    activeGroupId: null,
    // Saved tournament-settings templates. Cloud-synced (via the user doc's
    // otherState in firebase-auth.js, hydrated in app.js loadFullState) with a
    // device-local localStorage mirror. Each: {id, name, createdAt, settings}
    templates: [],
    // Delete markers for templates, so a delete here isn't resurrected by a
    // device that still has the template (see js/utils/user-data-merge.js).
    deletedTemplates: [],
    // Personal organization of the My Tournaments grid: folders, pins, and a
    // custom drag order. Cloud-synced + device-local mirror (same path as
    // templates). Shape:
    //   { folders: [{id, name, createdAt, pinned}],
    //     items: { [tid]: {folderId, pinned, order} },
    //     deletedFolders: [{id, at}] }
    tournamentOrg: { folders: [], items: {}, deletedFolders: [] },
    // Tie-breaks staged by "Choose Template" for a single-group create (the create form has
    // no tie-break inputs, so the submit handler reads this to honour the template).
    draftTiebreaks: null,
    // Welcome Page builder. `draftWelcomePage` = { blocks: [...] } being edited in the
    // create/edit modal (persisted to welcomePages/{id} on save); `activeWelcomePage` =
    // the page loaded for the currently watched/open tournament (for spectator render).
    // Neither is persisted to localStorage — they live in Firestore (welcomePages).
    draftWelcomePage: null,
    activeWelcomePage: null,
    // Registration link / API configuration staged while CREATING a tournament. The
    // link and the API keys need a saved tournament id, but the field configuration
    // does not, so the panel is offered during creation and its choices wait here
    // until the submit handler merges them into the new tournament.
    draftRegistrationApi: null,
    columnVisibility: {
        'col-title': true,
        'col-class': true,
        'col-club': true,
        'col-rating': true,
        'col-ids': true,
        'col-birthdate': true
    },
    userProfile: {
        givenName: '',
        lastName: '',
        country: '',
        displayName: '',
        club: '',
        theme: 'light',
        nameFormat: 'first-last'
    },
    viewedRoundIndex: 0,
    playerSort: {
        column: 'startNo',
        direction: 'asc'
    },
    tournamentFilter: 'all'
};


// Default settings used when there is no active tournament (or its fields are
// missing). Kept in one place so getActiveSettings() always returns a fully
// shaped object callers can read without extra `|| default` guards.
// A team tournament's own settings, kept in one nested object rather than spread
// across the flat settings so that every non-team tournament carries exactly one
// extra key and every team reader has a single place to look.
//
// `boards` is the number of players a team fields in a match — the "team size" the
// arbiter chooses, four by default, which is what the Norwegian club-team
// championship and most league play use.
//
// The defaults spell out FIDE's own: C.04.6 Type A colour preferences, match points
// primary with game points secondary, 2/1/0 for a match. In the TRF Tournament-Type
// Code Table that combination is exactly what the bare code FIDE_TEAM resolves to.
const TEAM_DEFAULTS = Object.freeze({
    boards: 4,
    pairingSystem: 'swiss',      // 'swiss' (C.04.6) | 'round-robin'
    primaryScore: 'match',       // 'match' | 'game' — what ranks the teams
    matchPointSystem: '2-1-0',
    colourPrefType: 'A',         // C.04.6 Type A | Type B | 'none'
    initialColour: 'white',      // which colour team pairing number 1 opens with
    reservesBottomOnly: false,   // some competitions bar holes above a fielded reserve
    // Whether a team CAPTAIN may set their own line-ups, or only the arbiter may.
    // A line-up is always fixed once its round is paired — that needs no setting, it
    // is what pairing the round means — so this governs the only thing genuinely worth
    // choosing: whether captains submit line-ups at all.
    captainsSetLineups: true,
    tiebreaks: ['gp', 'de', 'ttsb']
});

// Konrad system (js/core/konrad-engine.js). The defaults are Tromsø's: players may meet
// twice, and up to two rounds sat out are worth half a point each.
const KONRAD_DEFAULTS = Object.freeze({
    maxMeetings: 2,              // how many times two players may meet; 1 = never again
    byeValue: 'half',            // what a round sat out is worth: 'full' | 'half' | 'zero'
    maxScoringByes: 2,           // how many rounds sat out earn it; 0 = no limit
    countedRounds: 0             // only the best N rounds count in the standings; 0 = all
});

const DEFAULT_SETTINGS = Object.freeze({
    format: 'swiss',
    type: 'standard',
    roundsCount: 7,
    rounds: 7,
    pointSystem: '1-0.5-0',
    seedColor: 'white',
    tiebreaks: ['bh', 'bh1', 'de'],
    // Optional binding to an arbiter-owned rating list (playerDatabases/{dbId},
    // lists[].id). Empty means "use FIDE ratings", which is every tournament that
    // existed before custom databases. ratingListName is denormalised alongside
    // the id purely so the UI can label a rating without loading the database.
    ratingDbId: '',
    ratingListId: '',
    ratingListName: '',
    teamSettings: TEAM_DEFAULTS,
    konradSettings: KONRAD_DEFAULTS,
    // Prize categories (js/utils/categories.js) and the per-round schedule
    // (js/utils/round-schedule.js). Both start empty: a tournament that has not been
    // told it has junior prizes or a timetable does not have one, and an empty array
    // costs a stored tournament nothing.
    categories: [],
    roundSchedule: []
});

/**
 * Normalise anything shaped roughly like acceleration settings into a full object.
 *
 * Unlike the two normalisers below, this one does not live here: it lives beside the method
 * definitions in js/core/acceleration.js, because the list of methods and the arithmetic
 * that turns them into virtual points have to agree, and js/services/pairing-trf.js — a
 * classic script that cannot import from this module — needs the same function. Reached
 * through `window` like the pairing engines are, and lazily (inside a call, never at import
 * time) so that this module still loads in a context where the classic scripts have not run.
 */
export function accelerationSettingsFrom(src) {
    return window.Acceleration.accelerationFrom(src);
}

/**
 * Normalise anything shaped roughly like Konrad settings into a full object — for the same
 * reason as teamSettingsFrom() below: the form, a new group and a loaded tournament must all
 * read a blank or hand-edited value the same way.
 *
 * The two counts follow the participant maximum's convention: blank, zero or nonsense means
 * "no limit" / "every round", and is stored as 0.
 */
export function konradSettingsFrom(src) {
    const s = src || {};
    const count = (v) => {
        const n = parseInt(v, 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
    };
    return {
        maxMeetings: count(s.maxMeetings) || KONRAD_DEFAULTS.maxMeetings,
        byeValue: ['full', 'half', 'zero'].includes(s.byeValue) ? s.byeValue : KONRAD_DEFAULTS.byeValue,
        maxScoringByes: s.maxScoringByes === undefined ? KONRAD_DEFAULTS.maxScoringByes : count(s.maxScoringByes),
        countedRounds: count(s.countedRounds)
    };
}

/**
 * How many rounds count towards a player's score in these settings (Konrad's "best N of M"),
 * or 0 when every round does. Every ranking caller passes this on as `countedRounds`, so the
 * screen, the printouts and the exports agree on the order.
 */
export function countedRoundsFor(settings) {
    return settings && settings.format === 'konrad'
        ? konradSettingsFrom(settings.konradSettings).countedRounds
        : 0;
}

/**
 * The colour start number 1 opens with when nobody has chosen. NSF Monrad §4 gives it black,
 * and the Konrad system is Monrad underneath; FIDE's systems give it white.
 */
export function defaultSeedColor(format) {
    return format === 'monrad' || format === 'konrad' ? 'black' : 'white';
}

/**
 * Normalise anything shaped roughly like team settings into a full object.
 *
 * Exported because three places must agree on it — a new group's defaults, what the
 * create/edit form writes, and what a loaded tournament is read back as — and a team
 * tournament whose board count silently differed between them would pair one round
 * over four boards and expand the next over five.
 */
export function teamSettingsFrom(src) {
    const s = src || {};
    const boards = parseInt(s.boards, 10);
    return {
        boards: Number.isFinite(boards) && boards > 0 ? boards : TEAM_DEFAULTS.boards,
        pairingSystem: s.pairingSystem || TEAM_DEFAULTS.pairingSystem,
        primaryScore: s.primaryScore === 'game' ? 'game' : 'match',
        matchPointSystem: s.matchPointSystem || TEAM_DEFAULTS.matchPointSystem,
        colourPrefType: ['A', 'B', 'none'].includes(s.colourPrefType) ? s.colourPrefType : 'A',
        initialColour: s.initialColour === 'black' ? 'black' : 'white',
        reservesBottomOnly: !!s.reservesBottomOnly,
        captainsSetLineups: s.captainsSetLineups !== false,
        tiebreaks: Array.isArray(s.tiebreaks) && s.tiebreaks.length
            ? [...s.tiebreaks]
            : [...TEAM_DEFAULTS.tiebreaks]
    };
}

// Single source of truth for the ACTIVE tournament's settings (format, type,
// roundsCount/rounds, pointSystem, seedColor, tiebreaks).
//
//  - Multi-group tournament: returns the ACTIVE group's settings, normalising the
//    field-name mismatch — groups store the round count as `roundsCount`, while
//    legacy top-level readers use both `rounds` and `roundsCount`, so we expose
//    both keys with the same value.
//  - Legacy single-group tournament (no `groups` array): returns the top-level
//    fields on state.activeTournament.
//  - Cloud/spectator load (groups present in the meta doc but activeGroupId not
//    resolved to a group): falls back to the top-level fields, which the Firestore
//    meta doc carries denormalised from the active group.
//  - No active tournament: returns the defaults above.
//
// Returned object is a fresh plain copy — mutating it does NOT write back to the
// group or the tournament (use the group object / syncActiveSettingsToTopLevel for
// writes). Missing individual fields fall back to the defaults.
export function getActiveSettings() {
    const t = state.activeTournament;
    // `acceleration` is filled in here rather than sitting in DEFAULT_SETTINGS because its
    // normaliser is a classic script reached through `window`, and DEFAULT_SETTINGS is built
    // when this module is first imported — which in a test is before the shims are in place.
    if (!t) return { ...DEFAULT_SETTINGS, acceleration: accelerationSettingsFrom() };

    let src = t;
    if (t.groups && state.activeGroupId) {
        const g = t.groups.find(gr => String(gr.id) === String(state.activeGroupId));
        if (g) src = g;
    }

    // Groups only carry `roundsCount`; top-level docs may carry `rounds` (which,
    // after a cloud/spectator load, can be either the config count or — when it
    // was overwritten with the rounds ARRAY during assembly — a non-number). Prefer
    // roundsCount, then a numeric `rounds`, then the default.
    let roundsCount = src.roundsCount;
    if (roundsCount === undefined || roundsCount === null) {
        const asNum = parseInt(src.rounds);
        roundsCount = Number.isFinite(asNum) ? asNum : DEFAULT_SETTINGS.roundsCount;
    }

    return {
        format: src.format || DEFAULT_SETTINGS.format,
        type: src.type || DEFAULT_SETTINGS.type,
        roundsCount,
        rounds: roundsCount,
        pointSystem: src.pointSystem || DEFAULT_SETTINGS.pointSystem,
        seedColor: src.seedColor || DEFAULT_SETTINGS.seedColor,
        ratingDbId: src.ratingDbId || DEFAULT_SETTINGS.ratingDbId,
        ratingListId: src.ratingListId || DEFAULT_SETTINGS.ratingListId,
        ratingListName: src.ratingListName || DEFAULT_SETTINGS.ratingListName,
        // Always a fully shaped object, so a team reader never has to guard each field.
        // Non-team tournaments carry the defaults here and simply never read them.
        teamSettings: teamSettingsFrom(src.teamSettings),
        konradSettings: konradSettingsFrom(src.konradSettings),
        acceleration: accelerationSettingsFrom(src.acceleration),
        // Copied for the same reason as the tie-breaks below: a caller must not be able to
        // edit the group's prizes or timetable through the object it was handed.
        categories: Array.isArray(src.categories) ? src.categories.map(c => ({ ...c })) : [],
        roundSchedule: Array.isArray(src.roundSchedule) ? src.roundSchedule.map(r => ({ ...r })) : [],
        // Copy the array so callers can't mutate the group's/tournament's tie-breaks
        // through the returned object.
        tiebreaks: Array.isArray(src.tiebreaks) ? [...src.tiebreaks] : [...DEFAULT_SETTINGS.tiebreaks]
    };
}

// --- Teams ------------------------------------------------------------------
//
// WHERE TEAMS LIVE, and why not in `state` beside players and rounds. Players and
// rounds are mirrored into `state` because they are edited constantly and are large
// enough that Firestore keeps them in their own content documents. Teams are neither:
// a team is a name, a pairing number and a list of player ids, and it changes when
// somebody registers or a captain edits a squad.
//
// So teams are stored ON the group (or on the tournament itself, for a legacy
// single-group event) and read through the accessors below. That is not just simpler,
// it is what makes them persist at all: the Firestore meta doc is built by stripping
// `players` and `rounds` from each group and spreading everything else
// (js/services/firebase-auth.js, buildMetaDoc), so a new field on the group rides along
// with no change to that serializer — while a new top-level `state.teams` would need
// one, and that file is off-limits.

/**
 * The object that owns the active group's content: the active group when the
 * tournament has groups, otherwise the tournament itself. This is the same resolution
 * `rrPairingOrder` already uses; it lives here so the several callers that need it
 * stop repeating it.
 */
export function getActiveStore() {
    const t = state.activeTournament;
    if (!t) return null;
    if (t.groups && state.activeGroupId) {
        const g = t.groups.find(gr => String(gr.id) === String(state.activeGroupId));
        if (g) return g;
    }
    return t;
}

/** The active group's teams. Always an array; never a live reference to a missing one. */
export function getTeams() {
    const store = getActiveStore();
    return (store && Array.isArray(store.teams)) ? store.teams : [];
}

/** Replace the active group's teams. Callers still have to saveToLocalStorage(). */
export function setTeams(teams) {
    const store = getActiveStore();
    if (store) store.teams = teams;
    syncTeamCaptains();
}

/**
 * Rebuild `teamCaptains` — a flat { teamId: captainAddress } map at the ROOT of the
 * tournament — from every group's teams.
 *
 * IT EXISTS ONLY FOR THE SECURITY RULES, and it has to. A captain submitting a line-up is
 * checked against the tournament document (see the teamLineups rule in firestore.rules),
 * and the rules language cannot search an array of maps for the team with a given id. A
 * flat map it CAN index. Kept at the root because that is what a rule's get() reads; a
 * grouped tournament pools every group's teams into the one map, which is safe because
 * team ids are unique across the tournament.
 *
 * Derived, never edited: it is rebuilt from the teams on every change, so it cannot drift
 * away from them and grant a stale captain access to a team they no longer captain.
 */
function syncTeamCaptains() {
    const t = state.activeTournament;
    if (!t) return;
    const sources = (t.groups && t.groups.length) ? t.groups : [t];
    const map = {};
    sources.forEach(src => {
        (src.teams || []).forEach(team => {
            const email = (team.captainEmail || '').trim().toLowerCase();
            if (team.id && email) map[team.id] = email;
        });
    });
    t.teamCaptains = map;
}

/**
 * A new team, shaped the way TeamEngine and the TRF exporter expect.
 *
 * `teamNo` is the Team Pairing Number, and it is assigned once and then left alone —
 * C.04.6 art. 2.2 keeps TPNs fixed for the whole competition apart from late entries,
 * and the bye tie-break in art. 3.4 is decided on them, so renumbering mid-event would
 * quietly change who sits out.
 *
 * `squad` is the REGISTERED BOARD ORDER, strongest first. It is the order the TRF's
 * 013/310 records publish and the order every line-up is checked against, so its
 * meaning is positional, not incidental.
 */
export function createTeam({ name, club = '', fed = '', captainUid = '', captainEmail = '', captainName = '' } = {}) {
    const existing = getTeams();
    const nextNo = existing.reduce((max, t) => Math.max(max, parseInt(t.teamNo, 10) || 0), 0) + 1;
    return {
        id: 'team_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
        teamNo: nextNo,
        name: name || `Team ${nextNo}`,
        club,
        fed,
        // Shown in TRF record 310's nickname field; derived rather than asked for, and
        // editable afterwards like any other field.
        nickname: (name || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 5).toUpperCase(),
        strength: '',
        captainUid,
        captainEmail: (captainEmail || '').toLowerCase(),
        captainName,
        squad: [],
        // Per-round line-ups the captain has chosen: { [roundNo]: [playerId, ...] }.
        // A round with no entry falls back to the top of the squad.
        lineups: {},
        status: true
    };
}

/**
 * Put a player into the team of a given NAME, creating it if nobody has yet.
 *
 * This is how an online entry becomes a squad place. WHO BECOMES CAPTAIN: the first entry
 * to name a team is the one that creates it, and that person captains it — which is what
 * "registering a team" means. Every later entry naming the same team joins the squad
 * without changing the captain, so a team-mate entering themselves cannot take the role
 * from whoever set the team up. An arbiter can reassign it afterwards.
 *
 * Matching is by name, case- and whitespace-insensitively, because the registrant TYPED it
 * rather than picking from a list. Deliberately forgiving: a near-miss makes a second team
 * the arbiter can see and merge, which is a visible problem, where a stricter match would
 * silently strand the entry in a team of one.
 *
 * The squad grows in arrival order. That is the registered board order until somebody
 * rearranges it — there is nothing better to go on at this point, and an order that came
 * from arrival is easier to correct than one that came from nowhere.
 *
 * @returns {{ team: Object, created: boolean }} or null when there is no name to place by
 */
export function placeInTeamByName({ name, playerId, club = '', captainEmail = '', captainName = '' }) {
    const wanted = String(name || '').trim();
    if (!wanted || !playerId) return null;

    const key = (v) => String(v || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const teams = getTeams();
    let team = teams.find(t => key(t.name) === key(wanted));
    let created = false;

    if (!team) {
        team = createTeam({ name: wanted, club, captainEmail, captainName });
        setTeams([...teams, team]);
        created = true;
    }

    if (!Array.isArray(team.squad)) team.squad = [];
    if (!team.squad.includes(playerId)) team.squad.push(playerId);

    // The captain map the security rules read is derived from the teams, so it has to be
    // rebuilt whenever a team is created with one.
    syncTeamCaptains();
    return { team, created };
}

/** The team a player belongs to, or null. */
export function teamOfPlayer(playerId, teams = getTeams()) {
    return teams.find(t => (t.squad || []).includes(playerId)) || null;
}

// Mirror the active group's settings onto the top-level state.activeTournament
// fields. This is NOT the read path (use getActiveSettings()) — it exists only
// because the Firestore serializer in js/services/firebase-auth.js (off-limits)
// builds the meta doc from state.activeTournament's TOP-LEVEL format/type/
// pointSystem/seedColor/tiebreaks/roundsCount fields. Keeping them in step means
// the cloud payload (and the spectator/public rendering that reads it back) stays
// correct. Called from the two persistence entry points (load + save); no-op for
// legacy no-groups tournaments (their top-level fields are already canonical).
function syncActiveSettingsToTopLevel() {
    const t = state.activeTournament;
    if (!t || !t.groups || !state.activeGroupId) return;
    const g = t.groups.find(gr => String(gr.id) === String(state.activeGroupId));
    if (!g) return;
    t.format = g.format;
    t.type = g.type;
    t.rounds = g.roundsCount;
    t.roundsCount = g.roundsCount;
    t.pointSystem = g.pointSystem;
    t.seedColor = g.seedColor;
    t.tiebreaks = g.tiebreaks;
    t.teamSettings = g.teamSettings;
    t.konradSettings = g.konradSettings;
    t.acceleration = g.acceleration;
    // The prize categories and the timetable are mirrored for a reason the settings above
    // do not share: a SPECTATOR reads the meta document and never resolves a group, so
    // without this the public view of a grouped tournament would show no schedule and no
    // prize list at all.
    t.categories = g.categories;
    t.roundSchedule = g.roundSchedule;
    t.ratingDbId = g.ratingDbId;
    t.ratingListId = g.ratingListId;
    t.ratingListName = g.ratingListName;
}

/**
 * Write one setting onto whatever currently owns it, and keep the top-level mirror in
 * step — the active GROUP in a grouped tournament, the tournament itself in a legacy
 * single-group one.
 *
 * Exported because three editors now need the same four lines (tie-breaks, prize
 * categories, the round schedule), and a copy that wrote only the top level would look
 * right until the arbiter switched groups and found their work on the wrong one.
 */
export function writeActiveSetting(key, value) {
    const t = state.activeTournament;
    if (!t) return false;
    if (t.groups && state.activeGroupId) {
        const g = t.groups.find(gr => String(gr.id) === String(state.activeGroupId));
        if (!g) return false;
        g[key] = value;
        syncActiveSettingsToTopLevel();
    } else {
        t[key] = value;
    }
    return true;
}

// Which tournament (if any) is being WATCHED, remembered across reloads so a
// refresh comes back to it instead of dumping the viewer into their own context.
// Kept out of the tournament payload deliberately: it describes where this
// device is looking, not anything about the tournament.
const WATCHING_KEY = 'watchingTournamentId';

export function rememberWatchedTournament(id) {
    try {
        if (id) localStorage.setItem(WATCHING_KEY, String(id));
        else localStorage.removeItem(WATCHING_KEY);
    } catch (e) { /* localStorage may be unavailable */ }
}

export function getRememberedWatchedTournament() {
    try {
        return localStorage.getItem(WATCHING_KEY) || '';
    } catch (e) {
        return '';
    }
}

export function loadFromLocalStorage() {
    try {
        const savedTournament = localStorage.getItem('activeTournament');
        if (savedTournament && savedTournament !== 'undefined') {
            state.activeTournament = JSON.parse(savedTournament);
            
            // Check if tournament has groups
            if (state.activeTournament && state.activeTournament.groups && state.activeTournament.groups.length > 0) {
                // Validates the stored activeGroupId against the groups that
                // actually exist (it can be dangling — e.g. written by another
                // device before a group was deleted) and loads the players/
                // rounds mirror from the resolved group. Also mirrors the
                // group's settings onto the top-level fields for the Firestore
                // serializer; readers use getActiveSettings() instead.
                reconcileActiveGroupAfterLoad();
            } else {
                state.activeGroupId = null;
            }
        } else {
            state.activeTournament = null;
            state.activeGroupId = null;
        }

        // If not in a multi-group tournament, load legacy player/rounds data
        if (!state.activeGroupId) {
            const savedPlayers = localStorage.getItem('tournamentPlayers');
            if (savedPlayers && savedPlayers !== 'undefined') {
                state.players = JSON.parse(savedPlayers);
            } else {
                state.players = [];
            }

            const savedRounds = localStorage.getItem('tournamentRounds');
            if (savedRounds && savedRounds !== 'undefined') {
                state.rounds = JSON.parse(savedRounds);
                state.viewedRoundIndex = Math.max(0, state.rounds.length - 1);
            } else {
                state.rounds = [];
            }
        }

        // Tournament history is no longer stored in localStorage (it can exceed the
        // ~5MB quota for multi-group tournaments). It lives in IndexedDB and is
        // loaded once via initHistoryStorage() during app startup; state.history is
        // intentionally left untouched here so calls to loadFromLocalStorage() after
        // startup (e.g. exitWatchMode()) don't wipe out the already-loaded history.

        const savedColVis = localStorage.getItem('columnVisibility');
        if (savedColVis && savedColVis !== 'undefined') {
            state.columnVisibility = JSON.parse(savedColVis);
        }

        const savedProfile = localStorage.getItem('userProfile');
        if (savedProfile && savedProfile !== 'undefined') {
            state.userProfile = { ...state.userProfile, ...JSON.parse(savedProfile) };
        }

        const savedTemplates = localStorage.getItem('tournamentTemplates');
        if (savedTemplates && savedTemplates !== 'undefined') {
            state.templates = JSON.parse(savedTemplates);
        } else {
            state.templates = [];
        }

        const savedDeletedTemplates = localStorage.getItem('deletedTemplates');
        if (savedDeletedTemplates && savedDeletedTemplates !== 'undefined') {
            const parsed = JSON.parse(savedDeletedTemplates);
            state.deletedTemplates = Array.isArray(parsed) ? parsed : [];
        } else {
            state.deletedTemplates = [];
        }

        const savedOrg = localStorage.getItem('tournamentOrg');
        if (savedOrg && savedOrg !== 'undefined') {
            state.tournamentOrg = normalizeTournamentOrg(JSON.parse(savedOrg));
        } else {
            state.tournamentOrg = normalizeTournamentOrg(null);
        }
    } catch (e) {
        console.error('Error loading from localStorage:', e);
    }
}


export function saveToLocalStorage() {
    let archivalDataToPersist = null;

    if (!state.isSpectatorMode) {
        if (state.activeTournament) {
            // Rebuilt on every save, not only when the teams array is replaced: a captain's
            // address is often changed by editing a team in place, and this map is what the
            // security rules check a line-up submission against. Derived state that lags is
            // worse than none — it would leave a former captain able to submit.
            syncTeamCaptains();

            // Auto-save: sync current players and rounds back to active group
            if (state.activeTournament.groups && state.activeTournament.groups.length > 0) {
                const activeGroup = state.activeGroupId
                    ? state.activeTournament.groups.find(g => String(g.id) === String(state.activeGroupId))
                    : null;
                if (activeGroup) {
                    // The group a player is in IS their class, so record it here —
                    // the one point every roster change passes through, whether the
                    // player was added by hand, imported from a registration, or
                    // moved in by a merge. Blanks only; see stampClassFromGroup.
                    stampClassFromGroup(state.players, activeGroup.name);

                    activeGroup.players = structuredClone(state.players);
                    activeGroup.rounds = structuredClone(state.rounds);

                    // Mirror the active group's settings onto the top-level fields
                    // so the Firestore meta doc built below (and by firebase-auth.js)
                    // carries them. Readers use getActiveSettings(), not these.
                    syncActiveSettingsToTopLevel();
                } else {
                    // The cursor doesn't resolve to any group, so it is unknowable
                    // which group the players/rounds mirror belongs to — writing it
                    // into a guessed group would be cross-group corruption. Every
                    // load path is supposed to reconcile the cursor (see
                    // reconcileActiveGroupAfterLoad) before the first save, so
                    // reaching this means a load path regressed.
                    console.warn('saveToLocalStorage: activeGroupId does not match any group; skipping players/rounds sync-back.');
                }
            }

            // One structuredClone over a composite object instead of three separate
            // deep copies — a single traversal of the (potentially large) tournament
            // data. The clones stay fully independent of live state, as before.
            const cloned = structuredClone({
                data: state.activeTournament,
                players: state.players,
                rounds: state.rounds
            });
            const archivalData = {
                id: state.activeTournament.id,
                name: state.activeTournament.name,
                date: state.activeTournament.startDate,
                format: state.activeTournament.format,
                playerCount: state.players.length,
                roundCount: state.rounds.length,
                data: cloned.data,
                players: cloned.players,
                rounds: cloned.rounds
            };

            const existingIndex = state.history.findIndex(t => String(t.id) === String(archivalData.id));
            if (existingIndex !== -1) {
                state.history[existingIndex] = archivalData;
            } else {
                state.history.push(archivalData);
            }
            archivalDataToPersist = archivalData;

            trySetLocalStorage('activeTournament', JSON.stringify(state.activeTournament));
        } else {
            localStorage.removeItem('activeTournament');
        }

        trySetLocalStorage('tournamentPlayers', JSON.stringify(state.players));
        trySetLocalStorage('tournamentRounds', JSON.stringify(state.rounds));

        // History persistence now lives in IndexedDB (see js/services/history-db.js),
        // not localStorage — a full multi-group history array can exceed the ~5MB
        // localStorage quota. Only the single changed entry is written, fire-and-forget.
        if (archivalDataToPersist) {
            putHistoryEntry(archivalDataToPersist).catch(e => {
                console.error('Failed to persist tournament history entry to IndexedDB:', e);
            });
        }

        if (window.authApp && window.authApp.isAuthenticated()) {
            // Throttled sync with a LEADING edge: the first change writes immediately
            // (no delay window for isolated edits); rapid follow-ups coalesce into one
            // trailing write. A plain trailing debounce is FORBIDDEN here — it caused a
            // production data-loss incident (write never landed before refresh, stale
            // cloud then clobbered localStorage). The scheduler also keeps a persistent
            // dirty flag so a killed tab self-heals on next load; see firebase-auth.js.
            window.authApp.scheduleSave(state);
        }
    }

    trySetLocalStorage('columnVisibility', JSON.stringify(state.columnVisibility));
    trySetLocalStorage('userProfile', JSON.stringify(state.userProfile));
}

// Wraps localStorage.setItem with quota-exceeded handling. On QuotaExceededError
// (or any other setItem failure) it surfaces a single visible alert per session via
// the app's dialog utility, instead of letting the exception propagate and abort
// the rest of saveToLocalStorage() mid-write.
function trySetLocalStorage(key, value) {
    try {
        localStorage.setItem(key, value);
        return true;
    } catch (e) {
        console.error(`Error saving '${key}' to localStorage:`, e);
        const isQuotaError = e && (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED' || e.code === 22 || e.code === 1014);
        if (isQuotaError && !quotaAlertShown) {
            quotaAlertShown = true;
            if (window.Dialog && typeof window.Dialog.alert === 'function') {
                window.Dialog.alert(
                    tr('Local storage is full, so some data could not be saved on this device. Free up space (e.g. clear old browser data) or continue — your active tournament may not be fully backed up locally.'),
                    tr('Storage Full')
                );
            }
        }
        return false;
    }
}

// One-time async startup step for tournament-history persistence. Call once during
// app initialization (after loadFromLocalStorage()). It:
//   1. Migrates any legacy localStorage 'tournamentHistory' array into IndexedDB
//      (put each entry), then removes that localStorage key.
//   2. Loads all entries from IndexedDB into state.history.
//   3. Invokes onLoaded (if provided) so already-rendered UI (e.g. the "Open
//      tournament" history list) can re-render with the loaded data. Cheap and
//      safe to call even if that UI isn't visible yet.
// If IndexedDB is unavailable (private browsing, etc.) this leaves state.history
// as whatever it already was (in-memory only for the session) and warns once.
export async function initHistoryStorage(onLoaded) {
    try {
        const migrated = await migrateFromLocalStorage();
        const entries = await getAllHistoryEntries();

        if (historyOwnedByCloud) {
            // The sign-in path replaced state.history while this read was in
            // flight. Whatever came back is older than what we already hold, and
            // possibly another account's — see historyOwnedByCloud above.
        } else if (entries && entries.length > 0) {
            state.history = entries;
        } else if (migrated && migrated.length > 0) {
            // IDB unavailable but we at least have the migrated in-memory copy.
            state.history = migrated;
            console.warn('Tournament history could not be persisted to IndexedDB; keeping it in-memory for this session only.');
        }
    } catch (e) {
        console.warn('Error initializing tournament history storage; history will be in-memory only for this session.', e);
    }

    if (typeof onLoaded === 'function') {
        try {
            onLoaded();
        } catch (e) {
            console.error('Error in initHistoryStorage onLoaded callback:', e);
        }
    }
}

// Everything on this device that belongs to ONE signed-in account. Wiped when a
// different account signs in on the same browser — see discardOtherAccountData.
const ACCOUNT_SCOPED_KEYS = [
    'activeTournament',
    'tournamentPlayers',
    'tournamentRounds',
    'cloudSyncPending',
    'tournamentTemplates',
    'deletedTemplates',
    'tournamentOrg',
    'openPlayerDatabaseId',
    'watchingTournamentId',
    'userProfile',
    'columnVisibility'
];

/**
 * Drop every trace of the PREVIOUS account from this device: the working state
 * in memory, the localStorage mirror, and the IndexedDB tournament history.
 *
 * Signing out already does this, but it is not the only way an account changes
 * hands — a logout whose IndexedDB clear was cut short by the reload, a session
 * that expired, a browser restored from a crash. Anything left behind is not
 * merely visible to the next account: saveToLocalStorage() writes state.history
 * and the active tournament into the SIGNED-IN user's document, so the leftovers
 * become that account's own cloud data, and (before this) a player account
 * holding a stale arbiter history was auto-promoted to arbiter by the role
 * self-heal in firebase-auth.js.
 *
 * Sets historyOwnedByCloud so a still-in-flight IndexedDB read cannot put the
 * wiped entries back.
 */
export function discardOtherAccountData() {
    historyOwnedByCloud = true;

    state.activeTournament = null;
    state.activeGroupId = null;
    state.players = [];
    state.rounds = [];
    state.history = [];
    state.templates = [];
    state.deletedTemplates = [];
    state.tournamentOrg = normalizeTournamentOrg(null);
    state.isSpectatorMode = false;
    state.activeWelcomePage = null;
    state.viewedRoundIndex = 0;

    // The profile carries `role`, which decides which sidebar tabs exist. Keeping
    // the previous account's role is how a player ends up looking at an arbiter's
    // navigation. Theme survives: it is how this browser looks, not who is using
    // it, and flipping it mid-session is startling for no benefit.
    const theme = state.userProfile.theme;
    state.userProfile = {
        givenName: '', lastName: '', country: '', displayName: '', club: '',
        theme, nameFormat: 'first-last'
    };

    ACCOUNT_SCOPED_KEYS.forEach(key => {
        try { localStorage.removeItem(key); } catch (e) { /* ignore */ }
    });

    // Resolves true only when the IndexedDB history store was actually emptied.
    // Callers use that to decide whether the device can be considered clean.
    return clearHistoryStore().catch(e => {
        console.error('Failed to clear the previous account\'s tournament history:', e);
        return false;
    });
}

// Stamp the currently active tournament as closed (closedAt = now) and mirror
// the stamp onto its archived history entry (memory + IndexedDB). Must be called
// BEFORE any flow that swaps the active tournament out — the explicit Close
// button, opening another tournament, and creating/simulating a new one all
// count as "closing" for the "Recently closed/created" ordering
// (js/utils/tournament-sort.js getClosureTimestamp). No-op for drafts
// ('draft_...' ids) — they never appear in history.
export function stampActiveTournamentClosed() {
    const t = state.activeTournament;
    if (!t || String(t.id).startsWith('draft_')) return;
    t.closedAt = Date.now();
    const entry = state.history.find(h => String(h.id) === String(t.id));
    if (entry && entry.data) {
        entry.data.closedAt = t.closedAt;
        putHistoryEntry(entry).catch(e => {
            console.error('Failed to persist closedAt to IndexedDB:', e);
        });
    }
}

// Remove a tournament from history in both memory and IndexedDB. Callers that
// currently do `state.history = state.history.filter(...)` should switch to this
// so the IDB copy doesn't outlive the in-memory one (fire-and-forget on the IDB side).
export function removeFromHistory(id) {
    state.history = state.history.filter(t => String(t.id) !== String(id));
    deleteHistoryEntry(id).catch(e => {
        console.error('Failed to delete tournament history entry from IndexedDB:', e);
    });
}

// Wholesale-replace state.history (e.g. from a cloud snapshot in loadFullState)
// and mirror the same replacement into IndexedDB so the two stay in sync.
export function replaceHistory(entries) {
    historyOwnedByCloud = true;
    state.history = Array.isArray(entries) ? entries : [];
    putAllHistoryEntries(state.history).catch(e => {
        console.error('Failed to bulk-sync tournament history to IndexedDB:', e);
    });
}

// Best-effort trigger of the throttled cloud sync for user-scoped data (templates,
// tournamentOrg). These fields serialize to the user doc via `otherState` in
// firebase-auth.js's saveDataToFirestore, but their local mutators don't go
// through saveToLocalStorage(), so schedule the sync here. No-op when signed out.
function scheduleCloudSync() {
    try {
        if (window.authApp && typeof window.authApp.isAuthenticated === 'function'
            && window.authApp.isAuthenticated() && typeof window.authApp.scheduleSave === 'function') {
            window.authApp.scheduleSave(state);
        }
    } catch (e) {
        console.error('Error scheduling cloud sync:', e);
    }
}

// Persist the saved-settings templates. Cloud-synced (via the user doc) with a
// device-local localStorage mirror so they survive reloads offline.
export function saveTemplates() {
    try {
        localStorage.setItem('tournamentTemplates', JSON.stringify(state.templates));
        localStorage.setItem('deletedTemplates', JSON.stringify(state.deletedTemplates || []));
    } catch (e) {
        console.error('Error saving templates:', e);
    }
    scheduleCloudSync();
}

// Coerce an arbitrary parsed value into the canonical tournamentOrg shape so
// callers can read state.tournamentOrg.folders / .items / .deletedFolders
// without guards, even after a stale/partial cloud snapshot or hand-edited
// localStorage.
export function normalizeTournamentOrg(org) {
    const src = org && typeof org === 'object' ? org : {};
    return {
        folders: Array.isArray(src.folders) ? src.folders : [],
        items: src.items && typeof src.items === 'object' ? src.items : {},
        deletedFolders: Array.isArray(src.deletedFolders) ? src.deletedFolders : []
    };
}

// Persist the My Tournaments organization (folders/pins/custom order). Cloud-synced
// (via the user doc) with a device-local localStorage mirror.
export function saveTournamentOrg() {
    try {
        localStorage.setItem('tournamentOrg', JSON.stringify(state.tournamentOrg));
    } catch (e) {
        console.error('Error saving tournament organization:', e);
    }
    scheduleCloudSync();
}

// Which group id should be active for a tournament: its stored activeGroupId
// when that matches an existing group, otherwise the first group, or null for
// tournaments without groups. The stored value can point at a foreign group —
// a cloud copy carries whichever group the LAST WRITER (another device or
// co-arbiter) had open, and a deleted group can leave a dangling id — so it
// must always be validated against the groups that actually exist.
export function resolveActiveGroupId(t) {
    if (!t || !Array.isArray(t.groups) || t.groups.length === 0) return null;
    if (t.activeGroupId && t.groups.some(g => String(g.id) === String(t.activeGroupId))) {
        return t.activeGroupId;
    }
    return t.groups[0].id;
}

// Re-point the active-group cursor and reload the players/rounds mirror after
// state.activeTournament has been replaced WHOLESALE (cloud sync, opening from
// history, watch mode). Returns true when the tournament has groups (mirror
// loaded from the resolved group); false otherwise (caller assigns its own
// players/rounds).
//
// This must run before the first saveToLocalStorage() after such a swap: the
// swapped-in copy's TOP-LEVEL players/rounds belong to whichever group its
// writer had active, while state.activeGroupId still points wherever this
// device left it. Adopting the top-level arrays without re-pointing the cursor
// makes the next auto-save write one group's roster into another group —
// cross-group corruption that then syncs to the cloud.
export function reconcileActiveGroupAfterLoad() {
    const t = state.activeTournament;
    const gid = resolveActiveGroupId(t);
    state.activeGroupId = gid;
    if (!gid) return false;
    const g = t.groups.find(gr => String(gr.id) === String(gid));
    // Legacy-shape guard: some old docs carry the roster only denormalised at
    // the top level (groups hold settings only). When the resolved group IS
    // the tournament's own stored active group but has no players of its own,
    // adopt the top-level arrays — they were denormalised from exactly this
    // group. Never adopted on the fallback-to-first-group path: there the
    // top-level arrays belong to a group that no longer exists.
    if ((!Array.isArray(g.players) || g.players.length === 0)
        && String(t.activeGroupId) === String(gid)
        && Array.isArray(t.players) && t.players.length > 0) {
        g.players = structuredClone(t.players);
        if ((!Array.isArray(g.rounds) || g.rounds.length === 0) && Array.isArray(t.rounds)) {
            g.rounds = structuredClone(t.rounds);
        }
    }
    t.activeGroupId = gid;
    loadMirrorFromGroup(g);
    return true;
}

// Load the players/rounds working mirror from a group object. Deep copies so
// edits to the mirror only reach the group through the next save's sync-back.
function loadMirrorFromGroup(g) {
    state.players = structuredClone(g.players || []);
    state.rounds = structuredClone(g.rounds || []);
    state.viewedRoundIndex = Math.max(0, state.rounds.length - 1);
    syncActiveSettingsToTopLevel();
}

// Swapping groups context
export function selectGroup(groupId) {
    if (!state.activeTournament || !state.activeTournament.groups) return;

    // Never commit a cursor that doesn't resolve to an existing group — a
    // dangling id would detach the players/rounds mirror from every group
    // (saveToLocalStorage refuses to sync it back) and silently drop edits.
    const activeGroup = state.activeTournament.groups.find(g => String(g.id) === String(groupId));
    if (!activeGroup) return;

    // 1. Sync current state.players and state.rounds to the previously active group
    if (state.activeGroupId) {
        const prevGroup = state.activeTournament.groups.find(g => String(g.id) === String(state.activeGroupId));
        if (prevGroup) {
            prevGroup.players = structuredClone(state.players);
            prevGroup.rounds = structuredClone(state.rounds);
        }
    }

    // 2. Set new activeGroupId
    state.activeGroupId = groupId;
    state.activeTournament.activeGroupId = groupId;

    // 3. Load state.players and state.rounds from the new active group (also
    // mirrors its settings onto the top-level fields for the Firestore
    // serializer; readers use getActiveSettings() instead).
    loadMirrorFromGroup(activeGroup);
}

// Everything about a group that is a SETTING, as opposed to its identity (id,
// name), its schedule (useGroupDates/startDate/startTime) or its content
// (players, rounds). Three things have to agree on this list — what a new group
// inherits, what "Apply to all groups" copies, and what the group editor
// writes — so it lives in one place.
export const GROUP_SETTING_KEYS = Object.freeze([
    'format', 'type', 'roundsCount', 'pointSystem', 'seedColor', 'tiebreaks',
    'teamSettings', 'konradSettings', 'acceleration', 'categories', 'roundSchedule',
    'ratingDbId', 'ratingListId', 'ratingListName',
    'registrationOpen', 'registrationDeadline', 'registrationDeadlineTime',
    'requirePhone', 'registrationApproval', 'keepRegistrationOpen',
    'maxParticipants', 'waitingList',
    'requireNsfMembership', 'nsfSeries'
]);

// Read a full, normalised set of group settings out of anything shaped roughly
// like one: another group, a template's group entry, or a tournament's own
// top-level fields (a legacy single-group tournament, or the create modal's
// General tab after it has been read into a draft).
//
// Missing keys become the DEFAULTS FOR A NEW GROUP. Online registration comes
// back OFF, matching the create form's own switch: taking entries from the
// outside is a decision an organizer makes, not the resting state. Automatic
// approval stays on, because it only decides what happens to entries once
// somebody has asked for them. Nothing here reaches an existing group — those
// are only ever read with their own stored value, and callers that must preserve
// "unset means off" (the legacy migration below) copy the fields verbatim
// instead of going through this.
export function groupSettingsFrom(src) {
    const s = src || {};

    // Groups store `roundsCount`; a tournament's top level may only carry
    // `rounds`, and after a cloud load that can even be the rounds ARRAY.
    let roundsCount = s.roundsCount;
    if (roundsCount === undefined || roundsCount === null) {
        const asNum = parseInt(s.rounds);
        roundsCount = Number.isFinite(asNum) ? asNum : 7;
    }

    const format = s.format || 'swiss';
    return {
        format,
        type: s.type || 'standard',
        roundsCount,
        pointSystem: s.pointSystem || '1-0.5-0',
        seedColor: s.seedColor || defaultSeedColor(format),
        tiebreaks: Array.isArray(s.tiebreaks) ? [...s.tiebreaks] : ['bh', 'bh1', 'de'],
        teamSettings: teamSettingsFrom(s.teamSettings),
        konradSettings: konradSettingsFrom(s.konradSettings),
        acceleration: accelerationSettingsFrom(s.acceleration),
        categories: Array.isArray(s.categories) ? s.categories.map(c => ({ ...c })) : [],
        roundSchedule: Array.isArray(s.roundSchedule) ? s.roundSchedule.map(r => ({ ...r })) : [],
        ratingDbId: s.ratingDbId || '',
        ratingListId: s.ratingListId || '',
        ratingListName: s.ratingListName || '',
        registrationOpen: !!s.registrationOpen,
        registrationDeadline: s.registrationDeadline || '',
        registrationDeadlineTime: s.registrationDeadlineTime || '23:59',
        requirePhone: !!s.requirePhone,
        registrationApproval: s.registrationApproval || 'auto',
        keepRegistrationOpen: !!s.keepRegistrationOpen,
        // 0 is "no limit": a tournament that has not been told it has a ceiling
        // does not have one. Kept as a number so the group editor, the template
        // and the Cloud Function never have to guess at '' vs '0'.
        maxParticipants: normalizeMaxParticipants(s.maxParticipants),
        waitingList: s.waitingList === true,
        // Checked against NSF's member register on the Players page, on the terms of the
        // series: 'ngp', 'bgp' or '' for any other tournament (js/utils/nsf-membership.js).
        requireNsfMembership: s.requireNsfMembership === true,
        nsfSeries: s.nsfSeries === 'ngp' || s.nsfSeries === 'bgp' ? s.nsfSeries : ''
    };
}

/*
 * A participant maximum as a number, with everything that means "no limit" —
 * absent, empty, zero, a typo — collapsed to 0.
 *
 * MUST agree with normalizeMaxParticipants() in functions/lib/registration-api.js:
 * that copy is the one that turns people away, this one only decides what the
 * settings modal shows and stores. tests/registration-capacity.test.js fails if
 * the two disagree.
 */
export const MAX_PARTICIPANTS_CEILING = 10000;

export function normalizeMaxParticipants(raw) {
    if (raw === null || raw === undefined || raw === '') return 0;
    const n = typeof raw === 'number' ? raw : parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.min(Math.floor(n), MAX_PARTICIPANTS_CEILING);
}

// Which group a NEW group should copy its settings from: the one the organizer
// is looking at in the settings modal, else the most recently added group, else
// the tournament's own fields. This is what makes "set the tournament up once"
// hold — every group added afterwards starts from what was configured, instead
// of from a hardcoded 7-round Swiss.
function inheritedGroupSettings() {
    const t = state.activeTournament;
    if (!t) return groupSettingsFrom(null);
    const groups = Array.isArray(t.groups) ? t.groups : [];
    const src = groups.find(g => String(g.id) === String(state.modalActiveGroupId))
        || groups[groups.length - 1]
        || t;
    return groupSettingsFrom(src);
}

// Copy one group's settings onto every OTHER group in the tournament. Names,
// dates, players and rounds are untouched — only the list above travels.
// Returns how many groups were changed.
export function applyGroupSettingsToAll(sourceGroupId) {
    const t = state.activeTournament;
    if (!t || !Array.isArray(t.groups) || t.groups.length < 2) return 0;
    const source = t.groups.find(g => String(g.id) === String(sourceGroupId));
    if (!source) return 0;

    const settings = groupSettingsFrom(source);
    let changed = 0;
    t.groups.forEach(g => {
        if (String(g.id) === String(source.id)) return;
        GROUP_SETTING_KEYS.forEach(key => {
            const val = settings[key];
            g[key] = Array.isArray(val) ? [...val] : val;
        });
        changed++;
    });

    // The top-level mirror the Firestore serializer reads is built from the
    // ACTIVE group, which may be one of the ones just rewritten.
    syncActiveSettingsToTopLevel();
    return changed;
}

/**
 * Stamp a group's name onto its players' `class` field.
 *
 * In a tournament with groups, the group a player entered IS their class, and
 * writing it down is what keeps that true afterwards: once two groups are
 * merged, the group is gone, but "which class was this player in" is exactly
 * what the arbiter still needs for prize lists, age classes and Grand Prix
 * tables. Deriving it from the current group would answer the wrong question.
 *
 * A class the arbiter typed is never overwritten — this only fills in blanks.
 */
export function stampClassFromGroup(players, groupName) {
    if (!groupName || !Array.isArray(players)) return;
    players.forEach(p => {
        if (p && !p.class) p.class = groupName;
    });
}

/**
 * Rename follow-up: players still carrying the group's OLD name as their class
 * get the new one. Anyone with a class of their own — or one inherited from a
 * group they were merged out of — is left alone.
 */
export function renameGroupClass(players, oldName, newName) {
    if (!oldName || !newName || oldName === newName || !Array.isArray(players)) return;
    players.forEach(p => {
        if (p && p.class === oldName) p.class = newName;
    });
}

/**
 * Move every player from one group into another and delete the emptied group.
 *
 * REFUSED ONCE EITHER GROUP HAS BEEN PAIRED. A merge changes who is in the
 * field, and the rounds already played were paired against a different field:
 * colour and float history, byes, and Buchholz all refer to opponents that the
 * merged group may not contain. There is no honest way to carry those forward,
 * so the arbiter is told to merge before pairing instead of being handed a
 * tournament whose standings quietly stopped meaning anything.
 *
 * Starting numbers are CLEARED for the whole merged group. They are assigned
 * per group, so both halves own a "1"; the pairing flow reassigns them (see
 * generateNextRound -> assignStartingNumbers) before the first round.
 *
 * Returns { ok, error, moved, targetName }.
 */
export function mergeGroups(sourceGroupId, targetGroupId) {
    const t = state.activeTournament;
    if (!t || !Array.isArray(t.groups)) return { ok: false, error: 'no_groups' };

    const source = t.groups.find(g => String(g.id) === String(sourceGroupId));
    const target = t.groups.find(g => String(g.id) === String(targetGroupId));
    if (!source || !target || String(source.id) === String(target.id)) {
        return { ok: false, error: 'bad_selection' };
    }

    // The ACTIVE group's players and rounds live in the state mirror, not on the
    // group object, until the next save — so read both through here or a merge
    // done straight after an edit would work from a stale roster.
    const isActive = (g) => String(g.id) === String(state.activeGroupId);
    const playersOf = (g) => (isActive(g) ? state.players : g.players) || [];
    const roundsOf = (g) => (isActive(g) ? state.rounds : g.rounds) || [];

    const paired = [source, target].filter(g => roundsOf(g).length > 0);
    if (paired.length > 0) {
        return { ok: false, error: 'rounds_exist', pairedNames: paired.map(g => g.name) };
    }

    const moving = structuredClone(playersOf(source));
    const kept = structuredClone(playersOf(target));
    stampClassFromGroup(moving, source.name);
    stampClassFromGroup(kept, target.name);

    // Same person entered in both groups would otherwise appear twice. Only an
    // identical id counts as the same entry — two people with the same name are
    // two people, and that is the arbiter's call, not this function's.
    const seen = new Set();
    const merged = [];
    [...kept, ...moving].forEach(p => {
        const key = String(p.id);
        if (seen.has(key)) return;
        seen.add(key);
        merged.push({ ...p, startNo: '' });
    });

    target.players = merged;
    target.rounds = [];
    t.groups = t.groups.filter(g => String(g.id) !== String(source.id));

    // Whichever group the cursor was on, it now belongs on the survivor: it was
    // either the group that just disappeared, or the one whose roster changed
    // underneath the mirror.
    if (isActive(source) || isActive(target) || !state.activeGroupId) {
        state.activeGroupId = target.id;
        t.activeGroupId = target.id;
        loadMirrorFromGroup(target);
    }

    return { ok: true, moved: moving.length, targetName: target.name };
}

/**
 * Close online registration for whatever is being paired, now that its first
 * round exists.
 *
 * A deadline is a promise about a DATE; the first pairing is a fact about the
 * tournament. Once boards are assigned, a new entry has no round 1 to play, no
 * colour history, and no place in a Swiss field that has already been sorted —
 * so an arbiter who set the deadline to "the morning of" and paired at 18:00
 * would otherwise keep taking entries into a tournament that has started. That
 * is the case this exists for, and it is why the deadline does not get a vote.
 *
 * `keepRegistrationOpen` is the deliberate opt-out — a club night that pairs
 * round 1 and lets latecomers in with a half-point bye is a real format, and it
 * has to stay possible.
 *
 * Writes to the ACTIVE GROUP for a multi-group tournament (its groups are
 * paired one at a time and each closes on its own first round), otherwise to
 * the tournament. Returns the name of what was closed, or '' if nothing was.
 */
export function closeRegistrationOnFirstPairing() {
    const t = state.activeTournament;
    if (!t) return '';

    const target = (Array.isArray(t.groups) && t.groups.length > 0 && state.activeGroupId)
        ? t.groups.find(g => String(g.id) === String(state.activeGroupId))
        : t;
    if (!target || !target.registrationOpen || target.keepRegistrationOpen) return '';

    target.registrationOpen = false;
    // Distinguishes "the arbiter switched this off" from "pairing switched it
    // off", so the settings UI can say which.
    target.registrationClosedByPairing = true;
    return target.name || t.name || '';
}

// Group registration / migration helper
export function addGroup(name, settings = null) {
    if (!state.activeTournament) return null;

    if (!state.activeTournament.groups) {
        // Migrate legacy single-group tournament to multi-group tournament.
        // Copied VERBATIM, defaults deliberately not applied: this group already
        // exists as a tournament, and gaining online registration because the
        // organizer added a second group would be a change nobody asked for.
        const legacyGroup = {
            id: 'group_' + Date.now() + '_A',
            name: tr('Group {letter}', { letter: 'A' }),
            format: state.activeTournament.format || 'swiss',
            type: state.activeTournament.type || 'standard',
            roundsCount: state.activeTournament.rounds || 7,
            pointSystem: state.activeTournament.pointSystem || '1-0.5-0',
            seedColor: state.activeTournament.seedColor || 'white',
            tiebreaks: state.activeTournament.tiebreaks || ['bh', 'bh1', 'de'],
            ratingDbId: state.activeTournament.ratingDbId || '',
            ratingListId: state.activeTournament.ratingListId || '',
            ratingListName: state.activeTournament.ratingListName || '',
            registrationOpen: !!state.activeTournament.registrationOpen,
            registrationDeadline: state.activeTournament.registrationDeadline || '',
            registrationDeadlineTime: state.activeTournament.registrationDeadlineTime || '23:59',
            requirePhone: !!state.activeTournament.requirePhone,
            requireNsfMembership: state.activeTournament.requireNsfMembership === true,
            nsfSeries: state.activeTournament.nsfSeries || '',
            registrationApproval: state.activeTournament.registrationApproval || 'review',
            keepRegistrationOpen: !!state.activeTournament.keepRegistrationOpen,
            useGroupDates: false,
            startDate: '',
            startTime: '09:00',
            players: structuredClone(state.players),
            rounds: structuredClone(state.rounds)
        };
        state.activeTournament.groups = [legacyGroup];
        state.activeGroupId = legacyGroup.id;
        state.activeTournament.activeGroupId = legacyGroup.id;
    }

    // An explicit settings object (templates, tests) wins; otherwise inherit
    // whatever this tournament is already configured as.
    const resolved = settings ? groupSettingsFrom(settings) : inheritedGroupSettings();

    // Create new group
    const newGroup = {
        id: 'group_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7),
        // E.g. Group B, Group C (localized: Gruppe B, Gruppe C)
        name: name || tr('Group {letter}', { letter: String.fromCharCode(65 + state.activeTournament.groups.length) }),
        ...resolved,
        useGroupDates: false,
        startDate: '',
        startTime: '09:00',
        players: [],
        rounds: []
    };

    state.activeTournament.groups.push(newGroup);
    return newGroup;
}
